<script lang="ts" module>
  // import { ApiError } from '@/teoCore/errors/ApiError';
  import { ApiError } from '@/teoCore/errors/ApiError';
  import CtaButton from '@/ui/elements/buttons/CTAButton.svelte';
  import DropDown from '@/ui/elements/dropdown/DropDown.svelte';
  import type { DropDownItem } from '@/ui/elements/dropdown/DropDown/DropDownSelect.svelte';
  import { useAtomValue } from '@/utils/jotai-svelte';
  import { ActiveText$, onMountWithActiveText, teoAtom, useTeoCore } from '@/utils/teo-context';

  import AbTestComponent from './ABTestComponent.svelte';
  import SentimentTestComponent from './SentimentTestComponent.svelte';

  const AnalysisInstructions$ = teoAtom(
    (get) => get(ActiveText$).analysis?.getInstructions() ?? [],
  );

  AnalysisInstructions$.onMount = onMountWithActiveText(({ refresh }, activeText) => {
    return activeText.events.addEventListener('gotAnalysis', refresh);
  });

  const AISuggestion$ = teoAtom((get) => get(ActiveText$).ai.lastSuggestionDebug);
  AISuggestion$.onMount = onMountWithActiveText(({ refresh }, activeText) => {
    return activeText.ai.events.addEventListener('aiSuggestionReceived', refresh);
  });
</script>

<script lang="ts">
  const core = useTeoCore();

  const instructions = useAtomValue(AnalysisInstructions$);
  const aiSuggestion = useAtomValue(AISuggestion$);

  let evaluationProfiles: DropDownItem[] = $state([]);
  let selectedEvaluationProfileId: number | null = $state(
    core.dynamicConfig.options.evaluationProfileId,
  );

  function setExpParserOption(useExpParser: boolean | undefined) {
    core.dynamicConfig.setStateValue('useExpParser', useExpParser ?? false);
    core.activeText.touch();
  }

  async function loadEvaluationProfiles() {
    evaluationProfiles = [];
    const result = await core.api.getEvaluationProfiles();
    if (!(result instanceof ApiError)) {
      evaluationProfiles = result.map((profile) => {
        return {
          label: profile.name,
          value: profile.id.toString(),
        };
      });
    }
  }

  function onEvaluationProfileSelect(item: string) {
    const profileId = parseInt(item);
    selectedEvaluationProfileId = profileId;
    core.dynamicConfig.setStateValue('evaluationProfileId', profileId);
    // core.activeText.touch();
  }

  // function updateDiffFull() {
  //   core.activeText.diff.update(core.activeText.text, true);
  // }

  // function updateDiffMarkers() {
  //   core.activeText.diff.update();
  // }

  // function showDiff() {
  //   core.activeText.diff.show();
  // }
  // function hideDiff() {
  //   core.activeText.diff.hide();
  // }
</script>

<details>
  <summary>
    <strong>Debug Buttons</strong>
  </summary>

  <div class="grid gap-2">
    <label class="flex items-center gap-2">
      <input
        type="checkbox"
        checked={core.dynamicConfig.options.useExpParser}
        onchange={(e) => setExpParserOption((e.target as HTMLInputElement).checked)}
      />
      Use ExpParser
    </label>
    <!-- <CtaButton variant="primary" onclick={updateDiffFull}>Set diff Text</CtaButton>
    <CtaButton variant="primary" onclick={updateDiffMarkers}>Update diff markers</CtaButton>
    <CtaButton variant="primary" onclick={showDiff}>Show diff</CtaButton>
    <CtaButton variant="primary" onclick={hideDiff}>Hide diff</CtaButton> -->
    Evaluation Profile:
    <div class="flex items-center gap-2">
      <DropDown
        class="w-full flex-1"
        items={evaluationProfiles ?? []}
        disabled={!evaluationProfiles || evaluationProfiles.length === 0}
        selected={selectedEvaluationProfileId ? selectedEvaluationProfileId.toString() : ''}
        onItemClick={onEvaluationProfileSelect}
      ></DropDown>
      <CtaButton
        variant="cta"
        icon="Sync"
        tooltip="Profile laden"
        class="ml-2"
        onclick={() => loadEvaluationProfiles()}
      ></CtaButton>
    </div>
  </div>
</details>
<hr class="mb-2 mt-2" />
<details>
  <summary>
    <strong>Handlunsanweisungen</strong>
  </summary>

  <div class="mt-2 flex h-full flex-col gap-2 overflow-auto">
    {#if instructions.current.length === 0}
      <div class="bg-c-bg text-c-text rounded-md p-2 text-sm">No instructions available</div>
    {/if}
    {#each instructions.current as instruction (instruction)}
      <div class="bg-c-bg text-c-text rounded-md p-2 text-sm">
        <div>
          {instruction.title}
        </div>
        <div>
          {instruction.priority_score.toFixed(2)}
          ({instruction.expected_tpi_change.toFixed(2)})
        </div>
      </div>
    {/each}
  </div>
</details>
<hr class="mb-2 mt-2" />
<details>
  <summary>
    <strong>Letzte KI Antwort</strong>
  </summary>
  <div class="mt-2 flex h-full w-full flex-col gap-2 overflow-auto">
    {#if aiSuggestion.current === null}
      <div class="bg-c-bg text-c-text rounded-md p-2 text-sm">No AI suggestions available</div>
    {:else if aiSuggestion.current.suggestions.length}
      <div class="bg-c-bg text-c-text rounded-md p-2 text-sm">
        <div>
          {aiSuggestion.current.suggestions[0]?.text}
        </div>
      </div>
    {:else if aiSuggestion.current.bad_results.length}
      <div class="bg-c-bg text-c-text rounded-md p-2 text-sm">
        <div>
          <strong>Bad Result:</strong>
          <br />
          {aiSuggestion.current.bad_results[0]?.text}
        </div>
      </div>
    {:else if aiSuggestion.current.error}
      <div class="bg-red-100 text-red-800 rounded-md p-2 text-sm">
        <strong>Error:</strong>
        {aiSuggestion.current.error_message}
      </div>
    {/if}
  </div>
</details>
<hr class="mb-2 mt-2" />
<details>
  <summary>
    <strong>Factor168</strong>
  </summary>
  <div class="mt-2 flex h-full w-full flex-col gap-2 overflow-auto">
    <SentimentTestComponent />
  </div>
  <hr class="mb-2 mt-2" />
  <div class="mt-2 flex h-full w-full flex-col gap-2 overflow-auto">
    <AbTestComponent />
  </div>
</details>
